import { connect, ConnectedProps } from 'react-redux'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../utils/hoc/withLoading'
import { ComponentTabObject } from '../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../utils/reducers'
import { CostBuyPayload } from '../../../../../../../../../../utils/reducers/company/costsBuy'
import BuyStepHeaderMainNoPermission from '../../../../../header/BuyStepHeaderMainNoPermission'
import BuyReadOnlyHeaderFinancialRows from '../../../../../header/BuyReadOnlyHeaderFinancialRows'
import BuyReadOnlyHeaderCosts from '../../../../../header/BuyReadOnlyHeaderCosts'
import BuyRealOnlyHeaderAccounts from '../../../../../header/BuyReadOnlyHeaderAccounts'
import useBuyInvoiceHeaderURLs from '../../../../../../../../../../utils/hooks/buys/orders/useBuyInvoiceHeaderURLs'
import BuyReadOnlyHeaderShipping from '../../../../../header/BuyReadOnlyHeaderShipping'
import { BuyTagsPayload } from '../../../../../../../../../../utils/reducers/buys/buyTags'
import BuyReadOnlyHeaderVariables from '../../../../../header/BuyReadOnlyHeaderVariables'
import { SellVariablePayload } from '../../../../../../../../../../utils/reducers/company/sellVariables'
import AppStyleDecider from '../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../../utils/reducers/getters'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import ITHeaderText from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { displayDate } from '../../../../../../../../../../utils/globals/date'
import { ITHeaderBuyOrderTagsNew } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../utils/globals/tailwind'
import { parseAddressToOneLine } from '../../../../../../../../../../utils/globals/address'
import { BuyInvoiceModelType } from '@teinor/erp/types/company/order/buy/buyInvoice'

type BuyInvoiceCreateNoPermissionStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxBuyInvoiceCreateNoPermissionStepITHeader & {}

const BuyInvoiceCreateNoPermissionStepITHeader: React.FC<BuyInvoiceCreateNoPermissionStepITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={BuyInvoiceCreateNoPermissionStepITHeaderDefault} NewComponent={BuyInvoiceCreateNoPermissionStepITHeaderNew} {...rest} />
}

const BuyInvoiceCreateNoPermissionStepITHeaderDefault: React.FC<BuyInvoiceCreateNoPermissionStepITHeaderProps> = ({ buyTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useBuyInvoiceHeaderURLs()
    const tabs: ComponentTabObject = {
        'main': {
            component: BuyStepHeaderMainNoPermission,
            text: t('dataInvoice', 'Datos de la factura'),
            extraProps: { buyTags }
        },
    }
    if(!rest.headerData?.hidePrices){
        tabs['financialRow'] = {
            component: BuyReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'buy.invoice',
            extraProps: { orderHeaderURLs }
        }
        tabs['costs'] = {
            component: BuyReadOnlyHeaderCosts,
            text: t('costsBuy', 'Gastos de la compra'),
            permission: 'buy.invoice',
            extraProps: { orderHeaderURLs }
        }
    }
    tabs['shipping'] = {
        component: BuyReadOnlyHeaderShipping,
        text: t('shipping', 'Transporte'),
        permission: 'buy.invoice',
        extraProps: { orderHeaderURLs }
    }
    tabs['accounts'] = {
        component: BuyRealOnlyHeaderAccounts,
        text: t('divideIntoChapters', 'Dividir en capítulos'),
        permission: 'buy.invoice',
        extraProps: { orderHeaderURLs }
    }
    tabs['variables'] = {
        component: BuyReadOnlyHeaderVariables,
        text: t('buyVariables', 'Variables de la compra'),
        permission: 'buy.invoice',
        extraProps: { orderHeaderURLs }
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const BuyInvoiceCreateNoPermissionStepITHeaderNew: React.FC<BuyInvoiceCreateNoPermissionStepITHeaderProps> = ({ headerData, buyTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const activeCurrencies = getActiveCurrenciesDictionary()
    let extraFields: FormElement<keyof BuyInvoiceModelType>[] = []
    if (headerData?.CustomerId) {
        extraFields.push({
            name: 'CustomerId',
            component: 'Custom',
            render: () => <ITHeaderText label={t('customer', 'Cliente')} value={headerData?.Customer?.name} />,
            hideSectionByDefault: true
        },)
    }

    const sections: FormRender<BuyInvoiceModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('customName', 'Nombre personalizado')} value={value} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateBuyOrder', 'Fecha de la compra')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderBuyOrderTagsNew selectedTags={extraProps.selectedTags} dictTags={buyTags} headerData={headerData || {}} onChange={async () => { }} {...rest} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} readOnly={true} />)
                    },
                    extraProps: { selectedTags: headerData?.tags },
                    hideSectionByDefault: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedNumber', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'supplier_number_order',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierNumber', 'Número del proveedor')} value={value} />,
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierInvoiceS', 'Fact. proveedor')} value={value} />,
                },
                {
                    name: 'supplier_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateSupplier', 'Fecha del proveedor')} value={value ? displayDate(new Date(value)) : ''} />,
                },
                ...extraFields,
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('buyedBy', 'Comprado por')} value={headerData?.WorkingCenter.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WarehouseId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('warehouse', 'Almacen')} value={headerData?.Warehouse?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={parseAddressToOneLine(headerData?.BillingWorkingCenter)
                        } />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={async () => { }} sections={sections} headerData={headerData} getCompleteData={() => { }} />
    )
}

const BuyInvoiceCreateNoPermissionStepITHeaderDispatch = {
    setBuyTags: (payload: BuyTagsPayload) => ({ type: 'CHANGE_BUY_TAGS', payload }),
    setCostsBuy: (payload: CostBuyPayload) => ({ type: 'CHANGE_COSTBUY', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
}

export type ReduxBuyInvoiceCreateNoPermissionStepITHeader = ConnectedProps<typeof buyInvoiceCreateStepHeaderConnect>
const mapBuyInvoiceCreateStepHeaderConnector = (state: AllReduxPayloads) => ({ costsBuy: state.costsBuy, buyTags: state.buyTags, sellVariables: state.sellVariables })
const buyInvoiceCreateStepHeaderConnect = connect(mapBuyInvoiceCreateStepHeaderConnector, BuyInvoiceCreateNoPermissionStepITHeaderDispatch)
const buyInvoiceCreateStepHeaderConnectLoading = withLoading(BuyInvoiceCreateNoPermissionStepITHeader, [{ fetchUrl: '/api/app/config/cost/costBuy/getAllCostBuys', propName: 'costsBuy', setFunctionName: 'setCostsBuy' }, { fetchUrl: '/api/app/order/buy/tag/getAllBuyTags', propName: 'buyTags', setFunctionName: 'setBuyTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }])
const BuyInvoiceCreateNoPermissionStepITHeaderConnect = buyInvoiceCreateStepHeaderConnect(buyInvoiceCreateStepHeaderConnectLoading)

export default BuyInvoiceCreateNoPermissionStepITHeaderConnect